import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeft, ChevronRight } from '../Icon';
import styles from './DatePicker.module.css';

interface DatePickerProps {
  /** Value as YYYY-MM-DD, or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function toISO(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function parseISO(v: string): Date | null {
  const [y, m, d] = v.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function DatePicker({ value, onChange, placeholder, ariaLabel }: DatePickerProps) {
  const selectedDate = useMemo(() => parseISO(value), [value]);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [view, setView] = useState(() => selectedDate ?? new Date());
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openCal = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setDropUp(window.innerHeight - rect.bottom < 380);
    setView(selectedDate ?? new Date());
    setOpen(true);
  };

  const label = selectedDate
    ? selectedDate.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayISO = toISO(new Date());

  const cells: Array<{ day: number; iso: string; muted: boolean }> = [];
  // Leading days from previous month.
  const prevDays = new Date(year, month, 0).getDate();
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = prevDays - i;
    cells.push({ day: d, iso: toISO(new Date(year, month - 1, d)), muted: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, iso: toISO(new Date(year, month, d)), muted: false });
  }
  // Trailing days to fill the grid to full weeks.
  while (cells.length % 7 !== 0) {
    const d = cells.length - (firstDay + daysInMonth) + 1;
    cells.push({ day: d, iso: toISO(new Date(year, month + 1, d)), muted: true });
  }

  const pick = (iso: string) => {
    onChange(iso);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openCal())}
      >
        <CalendarIcon size={15} className={styles.icon} />
        {label ? (
          <span className={styles.value}>{label}</span>
        ) : (
          <span className={styles.placeholder}>{placeholder ?? 'Pick a date'}</span>
        )}
      </button>

      {open && (
        <div className={`${styles.pop} ${dropUp ? styles.popUp : ''}`} role="dialog">
          <div className={styles.head}>
            <button
              type="button"
              className={styles.nav}
              onClick={() => setView(new Date(year, month - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={15} />
            </button>
            <span className={styles.month}>
              {MONTHS[month]} {year}
            </span>
            <button
              type="button"
              className={styles.nav}
              onClick={() => setView(new Date(year, month + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          <div className={styles.weekRow}>
            {WEEKDAYS.map((w, i) => (
              <div key={i} className={styles.weekday}>
                {w}
              </div>
            ))}
          </div>

          <div className={styles.grid}>
            {cells.map((c, i) => (
              <button
                key={i}
                type="button"
                className={`${styles.day} ${c.muted ? styles.dayMuted : ''} ${
                  c.iso === todayISO ? styles.today : ''
                } ${c.iso === value ? styles.selected : ''}`}
                onClick={() => pick(c.iso)}
              >
                {c.day}
              </button>
            ))}
          </div>

          <div className={styles.footer}>
            <button type="button" className={styles.textBtn} onClick={() => pick(todayISO)}>
              Today
            </button>
            {value && (
              <button
                type="button"
                className={`${styles.textBtn} ${styles.clearBtn}`}
                onClick={() => pick('')}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
